// src/features/streams/components/StreamGrid.tsx

import { useRef, useCallback, useMemo, useState, useEffect, useLayoutEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Box, Checkbox, Menu, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import {
  IconBroadcast,
  IconChevronUp,
  IconChevronDown,
  IconCode,
  IconSettings,
  IconPlayerPlay,
  IconPlayerPause,
  IconTrash,
} from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { StreamGridRow as StreamGridRowComponent } from './StreamGridRow';
import { useSelection } from '@/shared/contexts/SelectionContext';
import {
  STREAM_ROW_HEIGHT,
  STREAM_GRID_MAX_VIEWPORT_HEIGHT_PX,
  STREAM_GRID_MAX_VIRTUAL_ROWS,
  STREAM_VIRTUALIZER_OVERSCAN,
} from '@/lib/constants/stream';
import type { StreamGridRow, StreamSortKey } from '@/lib/types/Stream';
import type { SortDirection } from '@/lib/types/common';
import { isPerfDebugEnabled, recordDuration, recordSample } from '@/shared/utils/perf';
import styles from './StreamGrid.module.css';

interface StreamGridProps {
  count: number;
  allIds: number[];
  getRow: (index: number) => StreamGridRow | null;
  sortKey: StreamSortKey;
  sortDirection: SortDirection;
  onSort: (key: StreamSortKey) => void;
  onPublish?: (stream: StreamGridRow) => void;
  onEncoderSetup?: (stream: StreamGridRow) => void;
  onActivateToggle?: (stream: StreamGridRow) => void;
  onDelete?: (stream: StreamGridRow) => void;
  initialScrollTop?: number;
  onScrollTopChange?: (scrollTop: number) => void;
  onFirstVisibleRowsPaint?: () => void;
}

interface HeaderColumn {
  key: string;
  label: string;
  sortable: boolean;
  sortKey?: StreamSortKey;
  align?: 'left' | 'center' | 'right';
}

const HEADER_COLUMNS: HeaderColumn[] = [
  { key: 'checkbox', label: '', sortable: false },
  { key: 'thumbnail', label: '', sortable: false },
  { key: 'title', label: 'Title', sortable: true, sortKey: 'title', align: 'left' },
  { key: 'liveStatus', label: 'Live Status', sortable: true, sortKey: 'liveStatus', align: 'center' },
  { key: 'location', label: 'Location', sortable: true, sortKey: 'location', align: 'left' },
  { key: 'status', label: 'Status', sortable: true, sortKey: 'status', align: 'center' },
  { key: 'published', label: 'Published', sortable: true, sortKey: 'published', align: 'right' },
  { key: 'updated', label: 'Updated', sortable: true, sortKey: 'updated', align: 'right' },
  { key: 'actions', label: '', sortable: false },
];

export function StreamGrid({
  count,
  allIds,
  getRow,
  sortKey,
  sortDirection,
  onSort,
  onPublish,
  onEncoderSetup,
  onActivateToggle,
  onDelete,
  initialScrollTop = 0,
  onScrollTopChange,
  onFirstVisibleRowsPaint,
}: StreamGridProps) {
  const navigate = useNavigate();
  const renderStartRef = useRef(performance.now());
  renderStartRef.current = performance.now();
  const perfDebugEnabled = useRef(isPerfDebugEnabled()).current;

  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const didRestoreScrollRef = useRef(false);
  const lastPaintTokenRef = useRef<string | null>(null);
  const didReportVirtualRowsViolationRef = useRef(false);
  const [viewportHeight, setViewportHeight] = useState(() => {
    if (typeof window === 'undefined') return 720;
    const estimated = Math.max(0, window.innerHeight - 220);
    return Math.min(estimated, STREAM_GRID_MAX_VIEWPORT_HEIGHT_PX);
  });

  const { selectedIds, isSelected, selectOnly, toggle, toggleRange, addToSelection, removeFromSelection } = useSelection();

  const allIdsRef = useRef<number[]>([]);
  allIdsRef.current = allIds;

  const virtualizer = useVirtualizer({
    count,
    // Seed the virtualizer's internal offset with the restored scroll position.
    // Its attach step syncs the element to that internal offset, so without this
    // a re-attach (e.g. StrictMode's dev double-mount) scrolls back to 0 and
    // undoes the scroll restore below.
    initialOffset: initialScrollTop,
    getScrollElement: () => parentRef.current,
    estimateSize: () => STREAM_ROW_HEIGHT,
    overscan: STREAM_VIRTUALIZER_OVERSCAN,
  });

  const virtualRows = virtualizer.getVirtualItems();
  const renderedVirtualRows = virtualRows.length > STREAM_GRID_MAX_VIRTUAL_ROWS
    ? virtualRows.slice(0, STREAM_GRID_MAX_VIRTUAL_ROWS)
    : virtualRows;

  // Viewport height measurement
  useLayoutEffect(() => {
    const containerEl = containerRef.current;
    const headerEl = headerRef.current;
    if (!containerEl || !headerEl || typeof ResizeObserver === 'undefined') return;

    const updateViewportHeight = () => {
      const measured = Math.max(0, containerEl.clientHeight - headerEl.offsetHeight);
      const fallback = typeof window !== 'undefined'
        ? Math.max(0, window.innerHeight - headerEl.offsetHeight - 140)
        : measured;
      const bounded = Math.min(
        measured > 0 ? measured : fallback,
        STREAM_GRID_MAX_VIEWPORT_HEIGHT_PX,
      );
      setViewportHeight((prev) => (Math.abs(prev - bounded) < 1 ? prev : bounded));
    };

    updateViewportHeight();

    const observer = new ResizeObserver(() => updateViewportHeight());
    observer.observe(containerEl);
    observer.observe(headerEl);
    return () => observer.disconnect();
  }, []);

  // Scroll restoration
  useLayoutEffect(() => {
    const scrollEl = parentRef.current;
    if (!scrollEl || didRestoreScrollRef.current) return;
    if (initialScrollTop > 0) {
      scrollEl.scrollTop = initialScrollTop;
      virtualizer.scrollToOffset(initialScrollTop);
    }
    didRestoreScrollRef.current = true;
  }, [initialScrollTop, virtualizer]);

  // Scroll tracking
  useEffect(() => {
    const scrollEl = parentRef.current;
    if (!scrollEl || !onScrollTopChange) return;
    const handleScroll = () => onScrollTopChange(scrollEl.scrollTop);
    handleScroll();
    scrollEl.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollEl.removeEventListener('scroll', handleScroll);
  }, [onScrollTopChange]);

  // Perf instrumentation
  useEffect(() => {
    if (!perfDebugEnabled) return;
    recordDuration('streams.grid.render', performance.now() - renderStartRef.current, {
      count,
      virtualRows: renderedVirtualRows.length,
      viewportHeight,
    });
  }, [count, renderedVirtualRows.length, viewportHeight, perfDebugEnabled]);

  // Containment violation reporting
  useEffect(() => {
    if (virtualRows.length <= STREAM_GRID_MAX_VIRTUAL_ROWS) {
      didReportVirtualRowsViolationRef.current = false;
      return;
    }
    if (didReportVirtualRowsViolationRef.current) return;
    didReportVirtualRowsViolationRef.current = true;
    recordSample('streams.virtual.rows.count', virtualRows.length, {
      count,
      limit: STREAM_GRID_MAX_VIRTUAL_ROWS,
      violation: true,
      viewportHeight,
    });
    console.error('[StreamGrid] Virtualization containment invariant violated', {
      virtualRows: virtualRows.length,
      limit: STREAM_GRID_MAX_VIRTUAL_ROWS,
      viewportHeight,
      totalRows: count,
    });
  }, [virtualRows.length, viewportHeight, count]);

  // First visible rows paint tracking
  useEffect(() => {
    if (count === 0 || renderedVirtualRows.length === 0) return;
    const paintToken = `${sortKey}:${sortDirection}:${count}`;
    if (lastPaintTokenRef.current === paintToken) return;
    lastPaintTokenRef.current = paintToken;
    onFirstVisibleRowsPaint?.();
  }, [count, renderedVirtualRows.length, sortKey, sortDirection, onFirstVisibleRowsPaint]);

  // Stable callback — reads from ref, so never changes identity
  const handleRangeSelect = useCallback(
    (id: number, index: number) => toggleRange(id, index, allIdsRef.current),
    [toggleRange],
  );

  const handleNavigate = useCallback(
    (href: string) => navigate({ to: href }),
    [navigate],
  );

  // --- Shared action menu state (single Menu instance for all rows) ---
  const [menuState, setMenuState] = useState<{
    opened: boolean;
    rowIndex: number | null;
  }>({ opened: false, rowIndex: null });

  const anchorRef = useRef<HTMLDivElement>(null);

  const handleMenuOpen = useCallback((index: number, buttonEl: HTMLElement) => {
    const rect = buttonEl.getBoundingClientRect();
    if (anchorRef.current) {
      anchorRef.current.style.position = 'fixed';
      anchorRef.current.style.top = `${rect.top}px`;
      anchorRef.current.style.left = `${rect.left}px`;
      anchorRef.current.style.width = `${rect.width}px`;
      anchorRef.current.style.height = `${rect.height}px`;
    }
    setMenuState({ opened: true, rowIndex: index });
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuState(prev => ({ ...prev, opened: false }));
  }, []);

  // Close menu on scroll
  useEffect(() => {
    const scrollEl = parentRef.current;
    if (!scrollEl || !menuState.opened) return;
    const onScroll = () => handleMenuClose();
    scrollEl.addEventListener('scroll', onScroll, { passive: true });
    return () => scrollEl.removeEventListener('scroll', onScroll);
  }, [menuState.opened, handleMenuClose]);

  // Get stream for active menu row
  const activeStream = menuState.rowIndex !== null ? getRow(menuState.rowIndex) : null;

  // Delete confirmation
  const handleRowDelete = useCallback((stream: StreamGridRow) => {
    modals.openConfirmModal({
      title: 'Delete Stream',
      children: (
        <Text size="sm">
          Are you sure you want to delete &ldquo;{stream.title}&rdquo;? This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => onDelete?.(stream),
    });
  }, [onDelete]);

  // Header checkbox state
  // Header checkbox scoped to the VISIBLE (filtered) rows — select-all under a
  // search must not drop selections made under a previous search. Memoized so
  // scroll re-renders don't pay O(n).
  const visibleSelectedCount = useMemo(() => {
    if (selectedIds.size === 0) return 0;
    let n = 0;
    for (const id of allIds) {
      if (selectedIds.has(id)) n++;
    }
    return n;
  }, [allIds, selectedIds]);

  const allSelected = count > 0 && visibleSelectedCount === count;
  const someSelected = visibleSelectedCount > 0 && visibleSelectedCount < count;

  const handleHeaderCheckbox = () => {
    if (allSelected) {
      removeFromSelection(allIdsRef.current);
    } else {
      addToSelection(allIdsRef.current);
    }
  };

  const handleHeaderClick = (column: HeaderColumn) => {
    if (column.sortable && column.sortKey) {
      onSort(column.sortKey);
    }
  };

  // Empty state
  if (count === 0) {
    return (
      <Box ref={containerRef} className={styles.gridContainer}>
        <Box className={styles.emptyState}>
          <IconBroadcast size={48} stroke={1.5} opacity={0.5} />
          <Text size="lg" mt="md" fw={500}>No streams found</Text>
          <Text size="sm" c="dimmed" mt="xs">
            Try adjusting your search or filters
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box ref={containerRef} className={styles.gridContainer}>
      {/* Header row using CSS Grid */}
      <div ref={headerRef} className={`${styles.header} ${styles.gridColumns}`}>
        {HEADER_COLUMNS.map((col) => {
          if (col.key === 'checkbox') {
            return (
              <div key={col.key} className={`${styles.headerCell} ${styles.cellCheckbox}`}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={handleHeaderCheckbox}
                  aria-label="Select all"
                />
              </div>
            );
          }

          const isActive = col.sortable && col.sortKey === sortKey;

          return (
            <div
              key={col.key}
              data-col={col.key}
              className={`${styles.headerCell} ${col.sortable ? styles.sortableHeader : ''}`}
              style={{
                justifyContent: col.align === 'right' ? 'flex-end' :
                                col.align === 'center' ? 'center' : 'flex-start',
                display: 'flex',
                alignItems: 'center',
              }}
              onClick={() => handleHeaderClick(col)}
            >
              {col.label}
              {col.sortable && (
                <span className={`${styles.sortIcon} ${isActive ? styles.sortIconActive : ''}`}>
                  {isActive && sortDirection === 'asc' ? (
                    <IconChevronUp size={14} />
                  ) : isActive && sortDirection === 'desc' ? (
                    <IconChevronDown size={14} />
                  ) : (
                    <IconChevronDown size={14} style={{ opacity: 0.3 }} />
                  )}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Scrollable body with virtualization */}
      <div
        ref={parentRef}
        className={styles.body}
        style={viewportHeight > 0 ? { height: `${viewportHeight}px` } : undefined}
      >
        <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
          {renderedVirtualRows.map(virtualRow => {
            const stream = getRow(virtualRow.index);
            if (!stream) return null;

            const selected = isSelected(stream.mediaId);

            return (
              <StreamGridRowComponent
                key={virtualRow.key}
                stream={stream}
                index={virtualRow.index}
                isSelected={selected}
                onSelectOnly={selectOnly}
                onToggle={toggle}
                onRangeSelect={handleRangeSelect}
                onMenuOpen={handleMenuOpen}
                onNavigate={handleNavigate}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: virtualRow.size,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              />
            );
          })}
        </div>
      </div>

      {/* Single shared action menu — positioned at clicked row's kebab button */}
      <Menu
        opened={menuState.opened}
        onChange={(opened) => { if (!opened) handleMenuClose(); }}
        shadow="md"
        width={200}
        position="bottom-end"
      >
        <Menu.Target>
          <div ref={anchorRef} style={{ position: 'fixed', pointerEvents: 'none', width: 0, height: 0 }} />
        </Menu.Target>
        <Menu.Dropdown>
          {activeStream && (
            <>
              <Menu.Item
                leftSection={<IconCode size={14} />}
                onClick={() => { handleMenuClose(); onPublish?.(activeStream); }}
                disabled={activeStream.statusLabel !== 'Active'}
              >
                Publish
              </Menu.Item>
              <Menu.Item
                leftSection={<IconSettings size={14} />}
                onClick={() => { handleMenuClose(); onEncoderSetup?.(activeStream); }}
              >
                Encoder Setup
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                leftSection={activeStream.isActive
                  ? <IconPlayerPause size={14} />
                  : <IconPlayerPlay size={14} />}
                onClick={() => { handleMenuClose(); onActivateToggle?.(activeStream); }}
              >
                {activeStream.isActive ? 'Deactivate' : 'Activate'}
              </Menu.Item>
              <Menu.Item
                color="red"
                leftSection={<IconTrash size={14} />}
                onClick={() => { handleMenuClose(); handleRowDelete(activeStream); }}
              >
                Delete
              </Menu.Item>
            </>
          )}
        </Menu.Dropdown>
      </Menu>
    </Box>
  );
}
